import type { FeaturedVideo } from '@/shared/types';
import type { StyleSection as StyleSectionData } from '@/features/landing/services/styleSections';
import { BrowseCard } from '@/features/browse/components/BrowseCard';
import { Button, ChevronRightIcon } from '@/shared/ui';

/* One row. A landing page's job is to show that there IS more, not all of it --
   past four tiles the band starts pushing the next heading off the fold, and
   everything beyond is what "See all" is for. */
const VISIBLE_TILES = 4;

export type StyleSectionProps = {
  section: StyleSectionData;
  selectedId?: string;
  onSelect: (video: FeaturedVideo) => void;
  onToggleSave: (video: FeaturedVideo) => void;
  /** Whether this viewer has the video in their library. A predicate rather than a
      list: the band does not need to know how saved-ness is stored, only how to ask. */
  isSaved?: (video: FeaturedVideo) => boolean;
  /** Opens the full band. Omitted while there is nothing more to show. */
  onSeeAll?: (section: StyleSectionData) => void;
};

/**
 * One titled band of continuously-playing videos, laid out as columns rather than
 * a row.
 *
 * The reference (openart.ai) runs a masonry of fixed-width columns whose cards keep
 * their own heights, all clips playing at once — measured at 233px wide with a 24px
 * gutter and heights from 131 to 414. The effect is a wall of motion you scan
 * rather than a carousel you page through, which is the right shape for "look what
 * this makes" and the wrong one for "find the thing you saved".
 *
 * Cards are `BrowseCard` in `motion` mode, so the tiles are the app's tiles —
 * same radius, same title treatment, same badges. Only the container differs.
 */
export function StyleSection({
  section,
  selectedId,
  onSelect,
  onToggleSave,
  isSaved,
  onSeeAll,
}: StyleSectionProps) {
  const shown = section.videos.slice(0, VISIBLE_TILES);
  const hasMore = section.videos.length > shown.length;

  return (
    <section className="style-band" aria-labelledby={`band-${section.key}`}>
      <div className="style-band__head">
        <div>
          <h3 className="style-band__title" id={`band-${section.key}`}>
            {section.heading}
          </h3>
          <p className="style-band__blurb">{section.blurb}</p>
        </div>

        {/* Only when there is something behind it. A "See all" that opens the same
            eight tiles is a dead end dressed as a promise. */}
        {hasMore && onSeeAll ? (
          <Button
            variant="secondary"
            iconRight={<ChevronRightIcon size={14} />}
            onClick={() => onSeeAll(section)}
          >
            See all
          </Button>
        ) : null}
      </div>

      {/* A four-up grid, not the masonry this started as. Equal-size tiles in
          rows read as a catalogue -- the eye compares them -- where ragged columns
          read as a collage. It also means every clip gets the same prominence,
          which is the honest presentation when they are all just videos. */}
      <div className="style-band__wall">
        {/* No wrapper element: the card is the grid item. The masonry this replaced
            needed one to stop tiles splitting across a column break; a grid has no
            such problem, and the extra div was a class nothing styled. */}
        {shown.map((video) => (
          <BrowseCard
            key={video.id}
            video={video}
            active={video.id === selectedId}
            saved={isSaved?.(video) ?? false}
            onSelect={onSelect}
            onToggleSave={onToggleSave}
            motion
          />
        ))}
      </div>
    </section>
  );
}
